import * as Styled from './ScanResultComponent.styles';

import { Check, StickyNote, X } from 'lucide-react';

import { FC } from 'react';
import { UndoCheckInButton } from './UndoCheckInButton';
import { getScannedTicket } from './services';
import { useQuery } from '@tanstack/react-query';

interface ScanResultProps {
	result: {
		type: 'success' | 'error';
		data?: any;
		errors?: any[];
	};
	onReset: () => void;
	eventId?: number;
}

const ALREADY_SCANNED = 'already validated';

const scanTime = (ticket: any, eventId?: number) => {
	const day = (ticket?.pass_validations || []).find((pv: any) => pv.event_id === eventId && pv.scanned_at);
	const at = day?.scanned_at || ticket?.scanned_at;
	return at ? new Date(at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : null;
};

const TicketCard: FC<{ ticket: any; holder?: string; eventId?: number; showScanTime?: boolean }> = ({
	ticket,
	holder,
	eventId,
	showScanTime,
}) => {
	const type = ticket?.TicketType;
	const scannedAt = showScanTime ? scanTime(ticket, eventId) : null;

	return (
		<Styled.TicketCard>
			<Styled.TypeBand $color={type?.color || '#3f3f46'} />
			<Styled.TicketBody>
				<Styled.TicketType>{type?.name || 'Ticket'}</Styled.TicketType>
				{type?.color_name && <Styled.ColorName>{type.color_name}</Styled.ColorName>}
				<Styled.Rows>
					{holder && (
						<Styled.Row>
							<span>Holder</span>
							<strong>{holder}</strong>
						</Styled.Row>
					)}
					{ticket?.seat_id && (
						<Styled.Row>
							<span>Seat</span>
							<strong>{ticket.seat_id}</strong>
						</Styled.Row>
					)}
					{scannedAt && (
						<Styled.Row>
							<span>Checked in at</span>
							<strong>{scannedAt}</strong>
						</Styled.Row>
					)}
					{ticket?.coupon_code > 0 && (
						<Styled.Row>
							<span>Promotion</span>
							<strong>{ticket.promocode_name || `Code ${ticket.coupon_code}`}</strong>
						</Styled.Row>
					)}
					<Styled.Row>
						<span>Ticket</span>
						<Styled.Mono>
							#{ticket?.ID}
							{ticket?.last_transaction_id ? ` · order ${ticket.last_transaction_id}` : ''}
						</Styled.Mono>
					</Styled.Row>
				</Styled.Rows>
			</Styled.TicketBody>
		</Styled.TicketCard>
	);
};

const RejectedTicket: FC<{ error: any; holder?: string; eventId?: number }> = ({ error, holder, eventId }) => {
	const ticketId = Number(error.ticket_id);
	const { data: ticket, isLoading } = useQuery(['scanner-ticket', ticketId], () => getScannedTicket(ticketId), {
		enabled: ticketId > 0,
		retry: false,
	});

	if (!ticketId) return null;
	if (isLoading) return <Styled.Loading>Loading ticket #{ticketId}…</Styled.Loading>;

	const fallback = { ID: ticketId, TicketType: error.ticket_type_name ? { name: error.ticket_type_name } : undefined };
	return <TicketCard ticket={ticket || fallback} holder={holder} eventId={eventId} showScanTime />;
};

export const ScanResultComponent: FC<ScanResultProps> = ({ result, onReset, eventId }) => {
	const { data } = result;
	const holder = data?.user_name || undefined;
	const isError = result.type === 'error';
	const errors = result.errors || [];
	const tickets = data?.tickets || [];

	const undoIds = isError
		? errors
				.filter((error) => error.ticket_id && `${error.error?.Error || ''}`.includes(ALREADY_SCANNED))
				.map((error) => Number(error.ticket_id))
		: tickets.map((ticket: any) => Number(ticket.ID));

	return (
		<Styled.ResultContainer>
			<Styled.Status $error={isError}>
				<Styled.StatusIcon>
					{isError ? <X size={28} strokeWidth={3} /> : <Check size={28} strokeWidth={3} />}
				</Styled.StatusIcon>
				<Styled.StatusText>
					<Styled.Title>{isError ? errors[0]?.error?.Error || 'Validation failed' : 'Checked in'}</Styled.Title>
					{!isError && tickets.length > 1 && <Styled.Subtitle>{tickets.length} tickets</Styled.Subtitle>}
				</Styled.StatusText>
			</Styled.Status>

			<Styled.Content>
				{isError
					? errors.map((error, index) => (
							<RejectedTicket key={error.ticket_id || index} error={error} holder={holder} eventId={eventId} />
					  ))
					: tickets.map((ticket: any) => (
							<TicketCard key={ticket.ID} ticket={ticket} holder={holder} eventId={eventId} />
					  ))}

				{data?.fan_notes && data.fan_notes.length > 0 && (
					<Styled.FanNotes>
						<Styled.FanNotesTitle>
							<StickyNote size={16} />
							Fan notes
						</Styled.FanNotesTitle>
						{data.fan_notes.map((n: { id: number; note: string }) => (
							<Styled.FanNoteItem key={n.id}>{n.note}</Styled.FanNoteItem>
						))}
					</Styled.FanNotes>
				)}
			</Styled.Content>

			<Styled.Actions>
				<Styled.PrimaryAction type="button" onClick={onReset}>
					Scan another ticket
				</Styled.PrimaryAction>
				{eventId !== undefined && <UndoCheckInButton eventId={eventId} ticketIds={undoIds} />}
			</Styled.Actions>
		</Styled.ResultContainer>
	);
};
